// File: src/products/equityManager/pages/EquityHomePage.js
// Equity Manager home page with summary statistics and quick action buttons

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from 'recharts';
import { 
    UserPlus, 
    Briefcase, 
    FileText, 
    ArrowRightLeft, 
    TrendingUp,
    DollarSign,
    Users,
    Building
} from 'lucide-react';
import { PIE_CHART_COLORS } from '../../../constants/app_constants';
import SortableTable from '../../../components/ui/SortableTable';
import CurrencyService from '../../../services/currencyService';

const EquityHomePage = ({ 
    companyData, 
    shareClasses, 
    selectedCompany, 
    shareholders,
    issuances,
    userProfile, 
    user,
    addMessage,
    refreshData
}) => {
    const navigate = useNavigate();
    const [clickedAction, setClickedAction] = useState(null);
    // Helper function to process data for display
    const getSummaryData = (issuances) => {
        console.log('EquityHomePage - getSummaryData called with:', { 
            issuances: issuances?.length || 0, 
            shareClasses: shareClasses?.length || 0,
            issuancesData: issuances 
        });
        
        if (!issuances || !shareClasses || issuances.length === 0) {
            return { totalShares: 0, totalValue: 0, classSummary: [], latestValuationPerShare: 0, companyValuation: 0 };
        }

        const latestIssuance = [...issuances].sort((a, b) => new Date(b.issue_date) - new Date(a.issue_date))[0];
        const latestValuationPerShare = latestIssuance?.price_per_share || 0;

        const totalShares = issuances.reduce((sum, i) => sum + (i.shares || 0), 0);
        const totalValue = issuances.reduce((sum, i) => sum + (i.total_value || (i.shares || 0) * (i.price_per_share || 0)), 0);
        const companyValuation = totalShares * latestValuationPerShare;

        const classSummary = Object.values(
            issuances.reduce((acc, issuance) => {
                const shareClass = shareClasses.find(sc => sc.id === issuance.share_class_id);
                const className = shareClass?.name || 'Unknown';
                if (!acc[className]) {
                    acc[className] = { name: className, totalShares: 0, totalValue: 0, percentage: 0 };
                }
                acc[className].totalShares += (issuance.shares || 0);
                acc[className].totalValue += (issuance.total_value || (issuance.shares || 0) * (issuance.price_per_share || 0));
                return acc;
            }, {})
        ).map(summary => ({
            ...summary,
            percentage: totalShares > 0 ? parseFloat(((summary.totalShares / totalShares) * 100).toFixed(2)) : 0,
        }));

        const result = { totalShares, totalValue, classSummary, latestValuationPerShare, companyValuation };
        console.log('EquityHomePage - getSummaryData result:', result);
        return result;
    };

    const summary = getSummaryData(issuances);

    const shareClassSummaryColumns = [
        { key: 'name', header: 'Class', isSortable: true },
        { key: 'totalShares', header: 'Shares', isSortable: true, isSummable: true, render: (row) => row.totalShares.toLocaleString() },
        { key: 'totalValue', header: 'Issued Capital', isSortable: true, isSummable: true, render: (row) => CurrencyService.formatAmount(row.totalValue, selectedCompany?.currency || 'USD') },
        { key: 'percentage', header: '%', isSortable: true, render: (row) => `${row.percentage}%` },
    ];

    // Quick action handlers - these will navigate to appropriate pages
    const handleQuickAction = (action) => {
        if (!selectedCompany) {
            addMessage?.('error', 'Please select a company first');
            return;
        }

        // Set clicked state for visual feedback
        setClickedAction(action);
        
        // Add a small delay for visual feedback
        setTimeout(() => {
            switch (action) {
                case 'add-director':
                    navigate('/equity/directors');
                    break;
                case 'add-shareholder':
                    navigate('/equity/shareholders');
                    break;
                case 'issue-shares':
                    navigate('/equity/shares');
                    break;
                case 'transfer-shares':
                    navigate('/equity/transact');
                    break;
                default:
                    console.log('Unknown quick action:', action);
            }
            setClickedAction(null);
        }, 150);
    };

    // Quick action button data
    const quickActions = [
        {
            id: 'add-director',
            title: 'Add Director',
            icon: Briefcase,
            color: 'blue',
            bgColor: 'bg-blue-50',
            iconColor: 'text-blue-600',
            hoverColor: 'hover:bg-blue-100'
        },
        {
            id: 'add-shareholder',
            title: 'Add Shareholder',
            icon: UserPlus,
            color: 'green',
            bgColor: 'bg-green-50',
            iconColor: 'text-green-600',
            hoverColor: 'hover:bg-green-100'
        },
        {
            id: 'issue-shares',
            title: 'Issue Shares',
            icon: FileText,
            color: 'purple',
            bgColor: 'bg-purple-50',
            iconColor: 'text-purple-600',
            hoverColor: 'hover:bg-purple-100'
        },
        {
            id: 'transfer-shares',
            title: 'Transfer Shares',
            icon: ArrowRightLeft,
            color: 'orange',
            bgColor: 'bg-orange-50',
            iconColor: 'text-orange-600',
            hoverColor: 'hover:bg-orange-100'
        }
    ];

    return (
        <div className="space-y-6">
            {/* Page Header */}
            <div className="mb-8">
                <h1 className="text-3xl font-bold text-gray-900 mb-2">
                    {selectedCompany ? `${selectedCompany.name} - Overview` : 'Equity'}
                </h1>
                <p className="text-gray-600">
                    {selectedCompany 
                        ? 'Manage your company\'s equity, cap table, and stakeholder relationships.'
                        : 'Select a company to view its equity overview and manage cap table data.'
                    }
                </p>
            </div>

            {/* Quick Actions Section */}
            <div className="bg-white rounded-lg shadow-sm border p-4 mb-8">
                <div className="flex items-center justify-between mb-4">
                    <h2 className="text-lg font-semibold text-gray-900">Quick Actions</h2>
                    {!selectedCompany && (
                        <span className="text-sm text-yellow-600 flex items-center">
                            <Building className="h-4 w-4 mr-1" />
                            Select a company to enable
                        </span>
                    )}
                </div>
                
                <div className="flex flex-wrap gap-3">
                    {quickActions.map((action) => (
                        <button
                            key={action.id}
                            onClick={() => handleQuickAction(action.id)}
                            disabled={!selectedCompany || clickedAction === action.id}
                            className={`
                                ${action.bgColor} ${action.hoverColor} 
                                px-4 py-3 rounded-lg border border-${action.color}-200
                                transition-all duration-200 
                                hover:shadow-md hover:scale-105
                                disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100
                                flex items-center space-x-3 min-w-0 flex-shrink-0
                                ${clickedAction === action.id ? 'scale-95 opacity-75' : ''}
                            `}
                        >
                            {clickedAction === action.id ? (
                                <div className="h-5 w-5 border-2 border-gray-300 border-t-blue-600 rounded-full animate-spin flex-shrink-0"></div>
                            ) : (
                                <action.icon className={`h-5 w-5 ${action.iconColor} flex-shrink-0`} />
                            )}
                            <div className="text-left min-w-0">
                                <div className="font-medium text-gray-900 text-sm">
                                    {action.title}
                                </div>
                            </div>
                        </button>
                    ))}
                </div>

            </div>

            {/* Company Data and Analytics */}
            {selectedCompany && (
                <>
                    {/* Summary Cards */}
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
                        <div className="bg-white p-6 rounded-lg shadow-sm border">
                            <div className="flex items-center">
                                <div className="p-2 bg-blue-100 rounded-lg">
                                    <FileText className="h-6 w-6 text-blue-600" />
                                </div>
                                <div className="ml-4">
                                    <p className="text-sm font-medium text-gray-600">Total Shares</p>
                                    <p className="text-2xl font-bold text-gray-900">
                                        {summary.totalShares.toLocaleString()}
                                    </p>
                                </div>
                            </div>
                        </div>

                        <div className="bg-white p-6 rounded-lg shadow-sm border">
                            <div className="flex items-center">
                                <div className="p-2 bg-green-100 rounded-lg">
                                    <DollarSign className="h-6 w-6 text-green-600" />
                                </div>
                                <div className="ml-4">
                                    <p className="text-sm font-medium text-gray-600">Total Issued Capital</p>
                                    <p className="text-2xl font-bold text-gray-900">
                                        {CurrencyService.formatAmount(summary.totalValue, selectedCompany?.currency || 'USD')}
                                    </p>
                                </div>
                            </div>
                        </div>

                        <div className="bg-white p-6 rounded-lg shadow-sm border">
                            <div className="flex items-center">
                                <div className="p-2 bg-purple-100 rounded-lg">
                                    <Users className="h-6 w-6 text-purple-600" />
                                </div>
                                <div className="ml-4">
                                    <p className="text-sm font-medium text-gray-600">Shareholders</p>
                                    <p className="text-2xl font-bold text-gray-900">
                                        {shareholders?.length || 0}
                                    </p>
                                </div>
                            </div>
                        </div>

                        <div className="bg-white p-6 rounded-lg shadow-sm border">
                            <div className="flex items-center">
                                <div className="p-2 bg-orange-100 rounded-lg">
                                    <TrendingUp className="h-6 w-6 text-orange-600" />
                                </div>
                                <div className="ml-4">
                                    <p className="text-sm font-medium text-gray-600">Latest Price</p>
                                    <p className="text-2xl font-bold text-gray-900">
                                        {CurrencyService.formatAmount(summary.latestValuationPerShare, selectedCompany?.currency || 'USD')}
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Charts and Analytics */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        {/* Pie Chart */}
                        <div className="bg-white p-6 rounded-lg shadow-sm border">
                            <h3 className="text-lg font-semibold mb-4">Share Distribution by Class</h3>
                            {summary.classSummary.length > 0 ? (
                                <ResponsiveContainer width="100%" height={300}>
                                    <PieChart>
                                        <Pie
                                            data={summary.classSummary}
                                            cx="50%"
                                            cy="50%"
                                            outerRadius={100}
                                            fill="#8884d8"
                                            dataKey="totalShares"
                                            label={({ name, percentage }) => `${name}: ${percentage}%`}
                                        >
                                            {summary.classSummary.map((entry, index) => (
                                                <Cell key={`cell-${index}`} fill={PIE_CHART_COLORS[index % PIE_CHART_COLORS.length]} />
                                            ))}
                                        </Pie>
                                        <Tooltip formatter={(value) => [value.toLocaleString(), 'Shares']} />
                                        <Legend />
                                    </PieChart>
                                </ResponsiveContainer>
                            ) : (
                                <div className="flex items-center justify-center h-[300px] text-gray-500">
                                    <div className="text-center">
                                        <FileText className="h-12 w-12 text-gray-400 mx-auto mb-2" />
                                        <p>No share classes found</p>
                                        <p className="text-sm">Add share issuances to see distribution</p>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Share Class Summary Table */}
                        <div className="bg-white p-6 rounded-lg shadow-sm border">
                            <h3 className="text-lg font-semibold mb-4">Share Class Summary</h3>
                            {summary.classSummary.length > 0 ? (
                                <SortableTable
                                    data={summary.classSummary}
                                    columns={shareClassSummaryColumns}
                                    entityType="share class"
                                />
                            ) : (
                                <div className="flex items-center justify-center h-[200px] text-gray-500">
                                    <div className="text-center">
                                        <p>No share classes found</p>
                                        <p className="text-sm">Add share issuances to see summary</p>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </>
            )}

            {/* No Company Selected State */}
            {!selectedCompany && (
                <div className="bg-gray-50 rounded-lg p-8 text-center">
                    <Building className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">Select a Company</h3>
                    <p className="text-gray-600">
                        Choose a company from the dropdown above to view its equity details and analytics.
                    </p>
                </div>
            )}
        </div>
    );
};

export default EquityHomePage;